'use client';

import {Button} from '@chakra-ui/button';
import {faCircleXmark} from '@fortawesome/pro-light-svg-icons/faCircleXmark';
import type {PopoverProps} from '@linx-security/ui';
import type {ReactNode} from 'react';
import React, {useState} from 'react';

import {
  Dot,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tag,
  Text,
  useDisclosure,
  useModalContext,
} from '@linx-security/ui';

interface RemediationStatusPopoverProps extends Omit<PopoverProps, 'children'> {
  status: keyof typeof statusTextMap;
  portalContainerRef?: React.RefObject<HTMLElement | null>;
  text?: string;
  onClick?: () => void;
  buttonText?: string;
  showTag?: boolean;
  children: ReactNode;
}

const renderTextMessage = (message: string) => {
  const parts = message.split(/(\*\*.*?\*\*)/);
  return parts.map((part, index) => {
    if (part.startsWith(`**`) && part.endsWith(`**`)) {
      return (
        <Text key={index} style={{fontWeight: `bold`}}>
          {part.slice(2, -2)}
        </Text>
      );
    } else {
      return <Text key={index}>{part}</Text>;
    }
  });
};

export const TagPopover: React.FC<RemediationStatusPopoverProps> = ({
  status,
  portalContainerRef,
  children,
  text,
  showTag = true,
  onClick,
  buttonText,
  ...rest
}) => {
  const modalContext = useModalContext();
  const {onOpen, onClose, isOpen} = useDisclosure();
  const [isLoading, _] = useState(false);

  const textColor = textColorMap[status] ?? `text.primary`;
  const statusText = statusTextMap[status];
  const isFailed = failedStatuses.includes(status);

  const handleOnPopoverTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement={`bottom`} {...rest}>
      <PopoverTrigger>
        <button onClick={handleOnPopoverTriggerClick}>{children}</button>
      </PopoverTrigger>
      <Portal containerRef={portalContainerRef ?? modalContext?.dialogRef}>
        <PopoverContent
          onClick={e => {
            e.stopPropagation();
          }}>
          <PopoverArrow />
          <PopoverBody
            sx={{
              padding: `space-050`,
              backgroundColor: `blue-500`,
              display: `flex`,
              flexDirection: `column`,
              gap: `space-200`,
            }}>
            {showTag && statusText && (
              <Tag
                variant={`secondary`}
                size={`sm`}
                sx={{width: `fit-content`, paddingX: `13`}}
                color={isFailed ? textColor : `text.state.success-bold`}
                {...rest}>
                <Dot fill={textColor} mr={`space-100`} size={`md`} />
                {statusText}
              </Tag>
            )}
            <HStack
              sx={{
                display: `flex`,
                justifyContent: `space-between`,
                alignItems: `center`,
              }}>
              <HStack>
                {isFailed && text && <Icon color={textColor} icon={faCircleXmark} />}
                {text && (
                  <Text
                    sx={{
                      display: `flex`,
                      gap: `space-050`,
                      flexWrap: `wrap`,
                      fontWeight: `medium`,
                    }}
                    color={textColor}>
                    {renderTextMessage(text)}
                  </Text>
                )}
              </HStack>
              {onClick && buttonText && (
                <Button
                  sx={{
                    color: textColor,
                    whiteSpace: `nowrap`,
                    minWidth: `fit-content`,
                    display: `flex`,
                    alignContent: `center`,
                    alignItems: `center`,
                    fontWeight: 500,
                  }}
                  variant={`unstyled`}
                  onClick={onClick}
                  isLoading={isLoading}>
                  {buttonText}
                </Button>
              )}
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const textColorMap: Record<string, string> = {
  FAILURE: `text.state.danger-bold`,
  FAILED: `text.state.danger-bold`,
  IN_PROGRESS: `text.state.danger-bold`,
  PENDING: `text.state.danger-bold`,
  SUCCESS: `text.state.success-bold`,
  RUNNING: `text.state.success-bold`,
  COMPLETED: `text.state.success-bold`,
  SUCCESSFUL: `text.state.success-bold`,
};

const failedStatuses = [`FAILURE`, `FAILED`];

const statusTextMap: Record<string, string> = {
  FAILURE: `Failure`,
  FAILED: `Failed`,
  IN_PROGRESS: `In Progress`,
  PENDING: `Pending`,
  SUCCESS: `Success`,
  RUNNING: `Running`,
  COMPLETED: `Completed`,
  SUCCESSFUL: `Successful`,
};
